/**
 * Full-screen adjudication flow at
 *   `/v4/go-lab/annotate/review/:recordId`.
 *
 * Sits outside `GoLabLayout` / `AnnotateLayout` so the manager gets a
 * focused, distraction-free surface (left pane: record context; right
 * pane: comparison on top + curated resolution + footer below). Per
 * `feature-spec.md` §3.5.2 / §3.5.3.
 *
 * Cascade matches B9's `AnnotationFlowPage` — record → collection (for
 * taxonomyKey) → pinned taxonomy version. The resolution selections are
 * seeded once (after the cascade lands) with the union of both
 * annotators' labels per dimension, so the manager curates down rather
 * than building from scratch. Submit writes a `Resolution`, flips the
 * record to `finalised`, fires a success toast, and navigates straight
 * back to the adjudication queue — no intermediate confirmation modal.
 */
import React, { useEffect, useMemo, useReducer, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '../../../ui/button';
import { useAuth } from '../../../../contexts/AuthContext';
import { useCollection } from '../datasets/hooks/useDatasets';
import { useVersionDetail } from '../hooks/useTaxonomy';
import { useDatasetRecord, useSubmitResolution } from './hooks/useAnnotation';
import { useUsersById } from './hooks/useUsers';
import RecordContextPane from './components/flow/RecordContextPane';
import ComparisonPane from './components/adjudication/ComparisonPane';
import ResolutionPane from './components/adjudication/ResolutionPane';


// ---------------------------------------------------------------------------
// Reducer.
// ---------------------------------------------------------------------------

interface AdjudicationState {
  /** dim id → ids of labels currently selected for the resolution. */
  resolutionSelections: Record<string, string[]>;
  /** dim id → whether the ComparisonPane card is expanded. */
  expandedDimensions: Record<string, boolean>;
  rationale: string;
  attendees: string[];
}

type AdjudicationAction =
  | { type: 'toggleLabel'; dimId: string; labelId: string }
  | { type: 'toggleExpanded'; dimId: string }
  | { type: 'setRationale'; value: string }
  | {
      type: 'seed';
      selections: Record<string, string[]>;
      expanded: Record<string, boolean>;
      attendees: string[];
    };

const initialState: AdjudicationState = {
  resolutionSelections: {},
  expandedDimensions: {},
  rationale: '',
  attendees: [],
};

const reducer = (
  state: AdjudicationState,
  action: AdjudicationAction,
): AdjudicationState => {
  switch (action.type) {
    case 'toggleLabel': {
      const current = state.resolutionSelections[action.dimId] ?? [];
      const has = current.includes(action.labelId);
      const next = has
        ? current.filter((id) => id !== action.labelId)
        : [...current, action.labelId];
      return {
        ...state,
        resolutionSelections: {
          ...state.resolutionSelections,
          [action.dimId]: next,
        },
      };
    }
    case 'toggleExpanded':
      return {
        ...state,
        expandedDimensions: {
          ...state.expandedDimensions,
          [action.dimId]: !(state.expandedDimensions[action.dimId] ?? false),
        },
      };
    case 'setRationale':
      return { ...state, rationale: action.value };
    case 'seed':
      return {
        ...state,
        resolutionSelections: action.selections,
        expandedDimensions: action.expanded,
        attendees: action.attendees,
      };
    default:
      return state;
  }
};


// ---------------------------------------------------------------------------
// Sub-screens for cascading loading/error states (mirrors B9).
// ---------------------------------------------------------------------------

const FullScreen: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex h-screen flex-col items-center justify-center bg-[var(--go-spring)] px-4">
    {children}
  </div>
);

const LoadingScreen: React.FC = () => (
  <FullScreen>
    <div
      className="h-12 w-12 animate-pulse rounded-full bg-muted"
      data-testid="adjudication-loading"
    />
    <p className="mt-3 text-[13px] text-muted-foreground">Loading record…</p>
  </FullScreen>
);

const ErrorScreen: React.FC<{
  message: string;
  onBack: () => void;
}> = ({ message, onBack }) => (
  <FullScreen>
    <p
      className="mb-4 text-center text-[14px] text-muted-foreground"
      data-testid="adjudication-error"
    >
      {message}
    </p>
    <Button variant="link" onClick={onBack}>
      Back to adjudication queue
    </Button>
  </FullScreen>
);


// ---------------------------------------------------------------------------
// Page.
// ---------------------------------------------------------------------------

const AdjudicationFlowPage: React.FC = () => {
  const params = useParams<{ recordId: string }>();
  const recordId = params.recordId ?? '';
  const navigate = useNavigate();
  const { user } = useAuth();

  // 1. Fetch the record (and its current annotations / resolution).
  const recordQuery = useDatasetRecord(recordId);

  // 1b. Batch-fetch display names for the two annotators on this record
  // (B15-FE). Single request per page render — threaded through to
  // ComparisonPane as a prop so the pane never fires its own user
  // lookup. Dedupes if both passes were authored by the same user
  // (defensive — §3.8 same-user prevention should make this impossible).
  const annotatorIds = useMemo(() => {
    const ids: string[] = [];
    const a = recordQuery.data?.firstAnnotation?.annotatorId;
    const b = recordQuery.data?.secondAnnotation?.annotatorId;
    if (a) ids.push(a);
    if (b && b !== a) ids.push(b);
    return ids;
  }, [
    recordQuery.data?.firstAnnotation?.annotatorId,
    recordQuery.data?.secondAnnotation?.annotatorId,
  ]);
  const usersQuery = useUsersById(annotatorIds);

  // 2. Resolve the taxonomy key via the record's collection.
  const collectionId = recordQuery.data?.record.collectionId;
  const collectionQuery = useCollection(collectionId ?? '');

  // 3. Fetch the pinned taxonomy version.
  const taxonomyKey = collectionQuery.data?.taxonomyKey ?? '';
  const versionId = recordQuery.data?.record.taxonomyVersionId ?? '';
  const versionQuery = useVersionDetail(taxonomyKey, versionId);

  // Local state.
  const [state, dispatch] = useReducer(reducer, initialState);

  // Submit mutation. Curried by record id.
  const submitResolution = useSubmitResolution(recordId);

  // Derive dimensions (empty list while loading) so memoised hooks stay
  // called every render — Rules of Hooks. Wrapped in `useMemo` so the
  // `?? []` fallback doesn't return a fresh array each render.
  //
  // Filter to the collection's selected dimension scope so the
  // adjudication pane only seeds + renders dimensions the collection
  // actually targets. Without this filter the seeding loop would
  // initialise resolution selections for out-of-scope dimensions and
  // `submitResolution` would persist the whole map as `gold_labels` —
  // contaminating the gold pool with entries the collection never
  // selected. Mirrors `AnnotationFlowPage.tsx`'s filter.
  const dimensions = useMemo(() => {
    const all = versionQuery.data?.taxonomyData?.dimensions ?? [];
    const scope = collectionQuery.data?.dimensions;
    if (!scope) return [];
    const allowed = new Set(scope);
    return all.filter((d) => allowed.has(d.id));
  }, [
    versionQuery.data?.taxonomyData?.dimensions,
    collectionQuery.data?.dimensions,
  ]);

  // Seed the resolution selections + expansion state once both the
  // record and the taxonomy have landed. Track seeding via a ref
  // rather than a derived signal like `state.attendees.length` - if a
  // future feature lets the manager remove themselves from
  // `attendees`, a length-based check would re-fire and overwrite
  // their selections. The ref flips once when seeding completes and
  // never again.
  const recordDetail = recordQuery.data;
  const hasSeeded = useRef(false);
  useEffect(() => {
    if (!recordDetail || dimensions.length === 0 || !user) return;
    if (hasSeeded.current) return;

    const first = recordDetail.firstAnnotation?.labels ?? {};
    const second = recordDetail.secondAnnotation?.labels ?? {};
    const selections: Record<string, string[]> = {};
    const expanded: Record<string, boolean> = {};
    for (const dim of dimensions) {
      const firstIds = first[dim.id] ?? [];
      const secondIds = second[dim.id] ?? [];
      const union: string[] = [];
      const seen = new Set<string>();
      for (const id of [...firstIds, ...secondIds]) {
        if (!seen.has(id)) {
          seen.add(id);
          union.push(id);
        }
      }
      selections[dim.id] = union;
      // Agreed dimensions collapse by default per spec §3.5.2.
      const firstSet = new Set(firstIds);
      const secondSet = new Set(secondIds);
      const dimAgreed =
        firstSet.size === secondSet.size &&
        Array.from(firstSet).every((l) => secondSet.has(l));
      expanded[dim.id] = !dimAgreed;
    }
    dispatch({
      type: 'seed',
      selections,
      expanded,
      attendees: [user.user_id],
    });
    hasSeeded.current = true;
  }, [recordDetail, dimensions, user]);

  // ----- Loading / error guards (all hooks called above) -----

  const goBackToQueue = () =>
    navigate('/v4/go-lab/annotate/review');

  if (
    recordQuery.isLoading ||
    (collectionId && collectionQuery.isLoading) ||
    (taxonomyKey && versionId && versionQuery.isLoading)
  ) {
    return <LoadingScreen />;
  }

  if (recordQuery.isError || !recordQuery.data) {
    return (
      <ErrorScreen
        message="We couldn't load this record."
        onBack={goBackToQueue}
      />
    );
  }

  const record = recordQuery.data.record;

  if (record.status !== 'disputed') {
    return (
      <ErrorScreen
        message="This record isn't disputed; nothing to adjudicate."
        onBack={goBackToQueue}
      />
    );
  }

  if (collectionQuery.isError || versionQuery.isError) {
    return (
      <ErrorScreen
        message="We couldn't load this record's taxonomy."
        onBack={goBackToQueue}
      />
    );
  }

  if (!record.taxonomyVersionId) {
    return (
      <ErrorScreen
        message="This record has no pinned taxonomy version; adjudication isn't possible."
        onBack={goBackToQueue}
      />
    );
  }

  if (!versionQuery.data) {
    return <LoadingScreen />;
  }

  if (!user) {
    return (
      <ErrorScreen
        message="You must be signed in to adjudicate."
        onBack={goBackToQueue}
      />
    );
  }

  const currentUser = {
    user_id: user.user_id,
    name: user.name && user.name.trim().length > 0 ? user.name : user.username,
  };

  // ----- Submit -----

  const onResolve = async () => {
    try {
      await submitResolution.mutateAsync({
        labels: state.resolutionSelections,
        rationale: state.rationale.trim(),
        attendees: state.attendees,
      });
      // Skip the intermediate "Record resolved" confirmation modal —
      // the adjudicator's intent is already clear, the BE accepted it,
      // and they want to get straight to the next record.
      toast.success('Record resolved & finalised');
      goBackToQueue();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to resolve record.';
      toast.error(message);
    }
  };

  return (
    <div className="flex h-screen flex-col bg-[var(--go-spring)]">
      {/* Monitor-style inline breadcrumb — parent link + current record id. */}
      <div className="flex items-center gap-1.5 border-b border-border bg-background px-4 py-3 text-xs text-muted-foreground md:px-6">
        <Link
          to="/v4/go-lab/annotate/review"
          className="font-medium text-primary hover:underline"
        >
          Adjudication
        </Link>
        <span aria-hidden>/</span>
        <span className="font-mono text-[12px]" aria-current="page">
          {record.id}
        </span>
      </div>

      <div className="grid flex-1 grid-cols-1 gap-4 overflow-hidden px-4 py-4 md:grid-cols-[35%_65%] md:gap-6 md:px-6 md:py-6">
        <RecordContextPane record={record} />

        <div className="flex min-h-0 flex-col gap-4 overflow-y-auto">
          <div className="rounded-2xl border bg-background p-4">
            <h2 className="mb-3 text-[13px] font-bold uppercase tracking-wider text-muted-foreground">
              Annotation comparison
            </h2>
            <ComparisonPane
              dimensions={dimensions}
              firstAnnotation={recordQuery.data.firstAnnotation}
              secondAnnotation={recordQuery.data.secondAnnotation}
              expandedDimensions={state.expandedDimensions}
              onToggleExpanded={(dimId) =>
                dispatch({ type: 'toggleExpanded', dimId })
              }
              namesByAnnotatorId={usersQuery.data}
            />
          </div>

          <div className="rounded-2xl border bg-background p-4">
            <h2 className="mb-3 text-[13px] font-bold uppercase tracking-wider text-muted-foreground">
              Resolution
            </h2>
            <ResolutionPane
              dimensions={dimensions}
              firstAnnotation={recordQuery.data.firstAnnotation}
              secondAnnotation={recordQuery.data.secondAnnotation}
              resolutionSelections={state.resolutionSelections}
              rationale={state.rationale}
              attendees={state.attendees}
              currentUser={currentUser}
              isSubmitting={submitResolution.isPending}
              onToggleLabel={(dimId, labelId) =>
                dispatch({ type: 'toggleLabel', dimId, labelId })
              }
              onRationaleChange={(value) =>
                dispatch({ type: 'setRationale', value })
              }
              onResolve={onResolve}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdjudicationFlowPage;
